import { useSelector } from 'react-redux';
import { formatDate, DATE_FORMAT } from '../utils/dateFormat.helper';
import { ACCOUNT_TYPES } from '../constants/accountType.constants';
import { ENTITY_SECONDARY_MARKET_MESSAGE } from '../utils/constant';

/**
 * Hook to determine if the current user is eligible to buy or sell
 * on the secondary market, based on the `secondaryMarketTradePreChecks`
 * field returned by the profile API.
 *
 * Buy eligibility:
 *   - User must have completed at least one primary (initial offering) trade
 *   - That trade must have been held for more than 60 days
 *
 * Sell eligibility (initial offering shares):
 *   - Secondary trade must have been held for more than 7 days
 *
 * Entity/Trust accounts are not supported on the Secondary Marketplace at
 * all (RDS-921) — this overrides both buy and sell eligibility regardless
 * of the checks above.
 *
 * Returns:
 *   isBuyEligible       {boolean}  Whether the user may place a secondary-market buy order
 *   isSellEligible      {boolean}  Whether the user may place a secondary-market sell order
 *   buyIneligibleMsg    {string}   Message to surface when the user is NOT buy-eligible
 *   sellIneligibleMsg   {string}   Message to surface when the user is NOT sell-eligible
 *   isEntityRestricted  {boolean}  Whether the restriction is specifically due to an Entity account
 */
const useSecondaryMarketEligibility = () => {
  const preChecks = useSelector(
    (state) => state.userData?.userData?.secondaryMarketTradePreChecks
  );
  const propertyDetails = useSelector((state) => state.propertyDetails?.data?.data);
  const accountType = useSelector((state) => state.userData?.userData?.accountType);
  const isEntityRestricted = accountType === ACCOUNT_TYPES.ENTITY;

  if (isEntityRestricted) {
    return {
      isBuyEligible: false,
      isSellEligible: false,
      buyIneligibleMsg: ENTITY_SECONDARY_MARKET_MESSAGE,
      sellIneligibleMsg: ENTITY_SECONDARY_MARKET_MESSAGE,
      isEntityRestricted: true,
    };
  }

  // If the backend hasn't returned the pre-checks yet, default to eligible
  // so that the existing disabled logic remains in charge until data arrives.
  if (!preChecks) {
    return {
      isBuyEligible: true,
      isSellEligible: true,
      buyIneligibleMsg: '',
      sellIneligibleMsg: '',
      isEntityRestricted: false,
    };
  }

  const {
    haveFirstPrimaryTrade,
    isPrimaryTradeOlderThan60Days,
    secondaryMarketBuyAccessDate,
  } = preChecks;

  // ── Buy eligibility — sourced from validatepin preChecks ─────────────────
  const isBuyEligible = !!(haveFirstPrimaryTrade && isPrimaryTradeOlderThan60Days);

  let buyIneligibleMsg = '';
  if (!haveFirstPrimaryTrade) {
    buyIneligibleMsg =
      'You must complete your first investment on the initial offerings marketplace and hold for 60 days before buying shares on the secondary market.';
  } else if (!isPrimaryTradeOlderThan60Days) {
    const formattedAccessDate = secondaryMarketBuyAccessDate
      ? formatDate(secondaryMarketBuyAccessDate, DATE_FORMAT.DATE_FORMAT_SHORT)
      : null;
    const accessDate = formattedAccessDate && formattedAccessDate !== 'Invalid date' ? formattedAccessDate : 'a future date';
    buyIneligibleMsg = `You can buy shares on the secondary market after your first investment has been held for 60 days. Secondary market access will be available on ${accessDate}.`;
  }

  // ── Sell eligibility ─────────────────────────────────────────────────────
  // Only the secondary trade 7-day hold check applies.
  // null means no secondary trade exists yet → 7-day check passes
  // false means secondary trade is < 7 days old → not eligible
  // true means secondary trade is > 7 days old → eligible
  const isSecondaryTradeOlderThan7Days = propertyDetails?.isSecondaryTradeOlderThan7Days;
  const nextSellAccessDate = propertyDetails?.nextSellAccessDate;

  const isSellEligible = isSecondaryTradeOlderThan7Days !== false;

  let sellIneligibleMsg = '';
  if (isSecondaryTradeOlderThan7Days === false) {
    const formattedSellDate = nextSellAccessDate
      ? formatDate(nextSellAccessDate, DATE_FORMAT.DATE_FORMAT_SHORT)
      : null;
    const sellDate = formattedSellDate && formattedSellDate !== 'Invalid date' ? formattedSellDate : 'a future date';
    sellIneligibleMsg = `Shares can be sold on the secondary market after ${sellDate}, once the shares have been held for more than 7 days.`;
  }

  return {
    isBuyEligible,
    isSellEligible,
    buyIneligibleMsg,
    sellIneligibleMsg,
    isEntityRestricted: false,
  };
};

export default useSecondaryMarketEligibility;
